import React, { Fragment, useEffect } from 'react';

import styles from 'buyside/components/presentation/JobVaultShowPage/jobVaultShowPageStyles';

import FlashMessage from 'buyside/components/presentation/FlashMessage/flashMessage';
import Banner from 'buyside/components/presentation/Banner/banner';
import LoadingSpinner from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import JobVaultShowHeader from 'buyside/components/presentation/JobVaultShowPage/JobVaultShowHeader/jobVaultShowHeader';
import JobVaultShowStats from 'buyside/components/presentation/JobVaultShowPage/JobVaultShowStats/jobVaultShowStats';
import JobVaultShowBlock from 'buyside/components/presentation/JobVaultShowPage/JobVaultShowBlock/jobVaultShowBlock';
import JobVaultShowList from 'buyside/components/presentation/JobVaultShowPage/JobVaultShowList/jobVaultShowList';
import JobVaultShowFooterBlock from 'buyside/components/presentation/JobVaultShowPage/JobVaultShowFooterBlock/jobVaultShowFooterBlock';
import ApplyButton from 'buyside/components/presentation/JobVaultShowPage/JobVaultShowApplyButton/jobVaultShowApplyButton';

import isLocalStorageNameSupported from 'buyside/util/isLocalStorageNameSupported';

import MapPinIcon from 'buyside/svgs/map_pin.svg';
import BookIcon from 'buyside/svgs/book.svg';
import CoinStackIcon from 'buyside/svgs/coin_stack.svg';
import BriefcaseIcon from 'buyside/svgs/briefcase.svg';

const formatYears = ({ fromYear, toYear }) => {
  if (!fromYear && !toYear) return null;
  if (fromYear === '8' && !toYear) return '8+ years';
  if (!fromYear && toYear === '0') return '0 years';
  return `${fromYear || 0} to ${toYear || '8+'} years`;
};

const JobVaultShowPage = ({ loading, error, userError, currentUser, jobPost }) => {
  const localStorageSupported = isLocalStorageNameSupported();
  const clearStorage = () => {
    if (localStorageSupported) window.localStorage.removeItem('jobVaultFlash');
  };

  useEffect(() => {
    window.addEventListener('beforeunload', clearStorage);

    return () => window.removeEventListener('beforeunload', clearStorage);
  });

  if (loading) {
    return (
      <div className={styles.loadingArea}>
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    throw error;
  } else if (userError) {
    if (userError.message.includes('401')) window.location.reload();
    throw userError;
  }

  const {
    id: currentUserId,
    jobPostsAppliedIds,
    hasJobAccess,
    canApplyToJobs,
    isAllocator,
    canCreateIdea,
    isHighProfile,
    hasActiveContract,
    profile: { id: profileId }
  } = currentUser;

  const {
    assets,
    actScore,
    city,
    degreesAndCertifications,
    description,
    disqualifiedReasons,
    employer,
    firmExperience,
    maximumGpa,
    minimumGpa,
    minimumSat,
    maximumSat,
    postedAt,
    regions,
    sectors,
    status,
    title,
    useCompanyInvestmentMandate,
    userId: authorId,
    yearsOfExperience
  } = jobPost;

  const isAuthor = currentUserId === authorId;
  const isApplicant = jobPostsAppliedIds && jobPostsAppliedIds.includes(jobPost.id);
  const noResearchRequired = !canCreateIdea || isHighProfile || hasActiveContract;

  const stats = [
    {
      data: city,
      Icon: MapPinIcon,
      label: 'location'
    },
    {
      data: employer.strategy,
      Icon: BookIcon,
      label: 'strategy'
    },
    {
      data: employer.aumRange,
      Icon: CoinStackIcon,
      label: 'aum range'
    },
    {
      data: employer.fundType,
      Icon: BriefcaseIcon,
      label: 'firm type'
    }
  ];

  const companyInvestmentMandate = [
    {
      label: 'asset class',
      data: employer.assets.join(', ')
    },
    {
      label: 'sector',
      data: employer.sectors.join(', ')
    },
    {
      label: 'region',
      data: employer.regions.join(', ')
    },
    {
      label: 'investment style',
      data: employer.investmentStyle
    }
  ];

  const positionInvestmentMandate = [
    {
      label: 'asset class',
      data: assets.join(', ')
    },
    {
      label: 'sector',
      data: sectors
    },
    {
      label: 'region',
      data: regions.join(', ')
    }
  ];

  const [fromYear, toYear] = yearsOfExperience ? yearsOfExperience.split('_') : [null, null];

  const minimumQualifications = [
    {
      label: 'years experience',
      data:
        (yearsOfExperience && yearsOfExperience.length && formatYears({ fromYear, toYear })) || null
    },
    {
      label: 'required firm experience',
      data: firmExperience.join(', ')
    },
    {
      label: 'education',
      data: degreesAndCertifications.join(', ')
    },
    {
      label: 'SAT score',
      data: minimumSat ? `${minimumSat} out of ${maximumSat}` : null
    },
    {
      label: 'ACT score',
      data: actScore ? String(actScore) : null
    },
    {
      label: 'GPA',
      data: minimumGpa ? `${minimumGpa} out of ${maximumGpa}` : null
    }
  ];

  const buttonProps = {
    canApplyToJobs,
    disqualifiedReasons,
    hasJobAccess,
    isApplicant,
    isAllocator,
    noResearchRequired,
    profileId,
    jobPostId: jobPost.id
  };

  const bigButton = {
    size: 'large',
    className: styles.applyButton
  };

  const isJobFilled = status === 'filled';

  let flashMessage;
  if (localStorageSupported && window.localStorage.getItem('jobVaultFlash')) {
    flashMessage = window.localStorage.getItem('jobVaultFlash');
  }

  return (
    <div>
      {isJobFilled && (
        <div className={styles.banner}>
          <Banner error>POSITION FILLED</Banner>
        </div>
      )}
      {status === 'inactive' && (
        <div className={styles.banner}>
          <Banner>
            Your post is awaiting review by a SumZero administrator. Once it has been approved, it
            will be published on the Job Vault.
          </Banner>
        </div>
      )}
      {flashMessage && (
        <Fragment>
          <FlashMessage>
            <div>{flashMessage}</div>
          </FlashMessage>
          <div className={styles.space} />
        </Fragment>
      )}
      <header className={styles.header}>
        <JobVaultShowHeader postedAt={postedAt} title={title} />
        {!isAuthor && !isJobFilled && <ApplyButton {...buttonProps} />}
      </header>
      <div>
        <JobVaultShowStats stats={stats} />
        {companyInvestmentMandate.some(({ data }) => data) && (
          <JobVaultShowBlock
            title="Company Investment Mandate"
            Component={JobVaultShowList}
            list={companyInvestmentMandate}
          />
        )}
        {!useCompanyInvestmentMandate && (
          <JobVaultShowBlock
            title="Position Investment Mandate"
            Component={JobVaultShowList}
            list={positionInvestmentMandate}
          />
        )}
        {minimumQualifications.some(({ data }) => data) && (
          <JobVaultShowBlock
            title="Minimum Qualifications"
            Component={JobVaultShowList}
            list={minimumQualifications}
          />
        )}
        <JobVaultShowBlock title="Company Description" text={employer.description} />
        <JobVaultShowBlock title="Job Description" text={description} />
        <JobVaultShowFooterBlock
          jobPostId={jobPost.id}
          canApplyToJobs={canApplyToJobs}
          isAuthor={isAuthor}
          profileId={profileId}
          disqualifiedReasons={disqualifiedReasons}
        />
        {!isAuthor && !isJobFilled && <ApplyButton {...buttonProps} {...bigButton} />}
      </div>
    </div>
  );
};

export default JobVaultShowPage;
